import React from 'react';
import { useLocation } from 'react-router-dom';
import { BottomNav } from '@parsina/framework';

import { C } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useAppNavigate } from '../../hooks/useAppNavigate.js';
import { NAV_ITEMS, activeNavFor, showsBottomNav } from '../../constants/nav.js';
import AppConnectivityBanner from '../common/AppConnectivityBanner.jsx';

// The mobile shell — what replaces StudentLayout below 768px.
//
// The rail is not collapsed here, it is GONE: a 68px mini-rail on a 360px
// viewport is a shrunken workstation, and it spends 19% of the width on chrome a
// thumb cannot comfortably reach. Each screen supplies its own gradient
// MobileHeader; this file owns two things only — the single page scroll, and the
// bar.

/*
 * `position: fixed; inset: 0` is LOAD-BEARING, not a style choice.
 *
 * styles.css sets `#root { display:flex; align-items:center; justify-content:
 * center; min-height:100vh }` over `body { background:#0f1923 }` — the frame the
 * pre-app auth screens are centred in. A flow-positioned mobile shell inherits
 * that and renders as a centred box floating on a dark page. Taking the shell out
 * of flow is what makes the app fill the phone.
 *
 * It is also the shape "one page-level scroll" needs: an `inset:0;
 * overflow:hidden` root with exactly ONE inner scroll region. (The kit's
 * ChatThread documents the same thing when it resolves its scroller — a mobile
 * shell is not the document.)
 */
const ROOT = {
  position: 'fixed',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  background: C.bg,
  // The TOP safe-area inset belongs to MobileHeader and the BOTTOM to BottomNav,
  // so what is left for the root is the notch's left and right — the landscape
  // case, where content would otherwise run under the rounded corners.
  paddingInlineStart: 'env(safe-area-inset-left, 0px)',
  paddingInlineEnd: 'env(safe-area-inset-right, 0px)',
  // No fontFamily: Meshkat is set on html/body in styles.css and inherited.
};

/*
 * The body does NOT scroll — each screen owns exactly ONE scroll region inside
 * it. A scrolling body here would make two: the screen's own scroller nested in
 * the shell's. It would also take away the choice of whether a screen's header
 * is pinned or scrolls away, which is decided by whether the screen puts its
 * MobileHeader inside its scroller — and that choice only exists if the shell is
 * not scrolling for it.
 */
const BODY = {
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

const MobileShell = ({ children }) => {
  const { t } = useTranslation();
  const navigate = useAppNavigate();
  const location = useLocation();

  const active = activeNavFor(location.pathname);
  // The bar hides on the two FULL-HEIGHT workspaces — the visit and the chat
  // page (core.md §7.0) — and renders everywhere else, the patient record
  // included: the record is a destination a student moves in and out of, and a
  // bar there is the shortest way back to the list. A bar on the chat page, by
  // contrast, would offer «پارسه» as an escape from an answer they have already
  // paid for and not yet read; both workspaces carry their own back button.
  const showNav = showsBottomNav(location.pathname);

  const items = React.useMemo(
    () =>
      NAV_ITEMS.map(({ id, labelKey, Icon }) => ({
        key: id,
        label: t(labelKey),
        icon: <Icon size={22} aria-hidden />,
        // No badges. The پارسه balance is a number that changes on every AI
        // action, and the kit renders a `badge` VERBATIM — a live count on a
        // navigation tab would jitter the bar under a thumb and would still be
        // stale the moment an answer landed. It has its own place on the credits
        // screen, and the assistant prints it beside the control that spends it.
      })),
    [t],
  );

  return (
    <div style={ROOT} data-testid="mobile-shell">
      {/* A <main>, so a screen-reader user has the same pair of landmarks the
          desktop rail and body give. */}
      <main aria-label={t('nav.landmark.content')} style={BODY} data-testid="mobile-scroll">
        {/* Once, above every routed screen; `flexShrink: 0` on the bar keeps it
            out of the screen's single scroll region (core.md §7.9). */}
        <AppConnectivityBanner pathname={location.pathname} />
        {children}
      </main>

      {showNav && (
        <BottomNav
          items={items}
          active={active}
          onSelect={(key) => navigate(key)}
          data-testid="student-bottom-nav"
          // The kit hardcodes a Persian `aria-label`, and its `...rest` spreads
          // AFTER it, so a caller-supplied one wins. This app ships `en` too.
          aria-label={t('nav.landmark.primary')}
        />
      )}
    </div>
  );
};

export default MobileShell;
